import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api, { getHHMailTemplates } from '../services/api';
import { apiService } from '../../services/apiService';
import { useAuthStore } from '../../store/authStore';
import { ReactComponent as LinkCopyIcon } from '../../assets/icons/link-copy.svg';
import { ReactComponent as LinkCopyCurrentIcon } from '../../assets/icons/link-copy-current.svg';
import { formatCandidateShortName } from '../utils/candidatePrivacy';

interface ResponseActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  responses: any[];
  vacancyId: string;
  positionId?: number | null;
  positionSlug?: string | null;
  aiScreeningEnabled?: boolean;
  onSuccess?: () => void;
  onRemoveResponses?: (responseIds: string[]) => void;
  initialActionId?: string | null;
  initialTemplateId?: string | null;
}

const ResponseActionModal: React.FC<ResponseActionModalProps> = ({
  isOpen,
  onClose,
  responses,
  vacancyId,
  positionId = null,
  positionSlug = null,
  aiScreeningEnabled = false,
  onSuccess,
  onRemoveResponses,
  initialActionId = null,
  initialTemplateId = null,
}) => {
  const [selectedAction, setSelectedAction] = useState<any | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
  const [message, setMessage] = useState('');
  const [processingAction, setProcessingAction] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });
  const [loadingTemplateText, setLoadingTemplateText] = useState(false);
  const [copiedLinkKey, setCopiedLinkKey] = useState<null | 'ai-screening' | 'hr-interview'>(null);
  const [hasQuestions, setHasQuestions] = useState(false);
  const [prefillTemplate, setPrefillTemplate] = useState<any | null>(null);

  const isBulkMode = responses.length > 1;
  const getResponseCandidateDisplayName = (response: any) => formatCandidateShortName({
    first_name: response.resume?.first_name,
    firstName: response.resume?.firstName,
    last_name: response.resume?.last_name,
    lastName: response.resume?.lastName,
    middle_name: response.resume?.middle_name,
    middleName: response.resume?.middleName,
    fullName: response.resume?.fullName || response.resume?.full_name,
    name: response.resume?.name,
    displayName: response.resume?.displayName,
  }, response.resume?.title || 'Кандидат');

  useEffect(() => {
    if (!positionId || !isOpen) return;
    apiService.getQuestions(positionId)
      .then(data => setHasQuestions((data?.questions?.length ?? 0) > 0))
      .catch(() => setHasQuestions(false));
  }, [positionId, isOpen]);

  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textArea);
        return ok;
      } catch {
        return false;
      }
    }
  };

  const handleCopyLink = async (key: 'ai-screening' | 'hr-interview', url: string) => {
    const ok = await copyToClipboard(url);
    if (!ok) {
      toast.error('Не удалось скопировать');
      return;
    }
    setCopiedLinkKey(key);
    toast.success('Скопировано');
    setTimeout(() => setCopiedLinkKey(null), 2000);
  };

  const loadTemplateText = async (template: { id?: string; name?: string; url?: string }): Promise<string> => {
    const normalize = (value: any) =>
      String(value ?? '')
        .replace(/\u00A0|\u202F/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    const selectedId = String(template?.id ?? '').trim();
    const selectedName = normalize(template?.name);
    const isNotFit = selectedName === normalize('Не подходит');
    const isNotFitAfterInterview = selectedName === normalize('Не подходит по итогам интервью');
    const preferredIds: string[] =
      isNotFit
        ? ['response_discard_mail', 'interview_discard_mail']
        : (isNotFitAfterInterview ? ['interview_discard_mail'] : []);
    let targetName = selectedName;

    if (selectedId || targetName || template?.url) {
      const data: any = await getHHMailTemplates();
      const raw = data?.templates ?? data;
      const list = Array.isArray(raw) ? raw : [];
      const bySelectedId = selectedId
        ? (list.find((t: any) => String(t?.id ?? '') === selectedId) ?? null)
        : null;
      const byId =
        preferredIds.length > 0
          ? (preferredIds
            .map((id) => list.find((t: any) => String(t?.id ?? '') === id))
            .find(Boolean) ?? null)
          : null;
      const byExact = list.find((t: any) => normalize(t?.name) === targetName);
      const found =
        bySelectedId
        ?? byId
        ?? byExact
        ?? (isNotFit
          ? (list.find((t: any) => normalize(t?.name).includes('не подходит') && normalize(t?.name).includes('отклик'))
            ?? list.find((t: any) => normalize(t?.name).includes('не подходит')))
          : null)
        ?? (isNotFitAfterInterview
          ? (list.find((t: any) => normalize(t?.name).includes('не подходит') && normalize(t?.name).includes('интервью'))
            ?? list.find((t: any) => normalize(t?.name).includes('не подходит')))
          : null);
      const text = (found?.text ?? '') as string;
      if (text) return text;
      console.warn('HH mail_templates: не найден текст по шаблону', {
        id: template?.id,
        name: template?.name,
        matchedName: found?.name
      });
    }

    if (!template?.url) return '';
    const match = template.url.match(/message_templates\/([^?]+)(?:\?topic_id=(\d+))?/);
    if (!match) return '';
    const [, templateType, topicId] = match;
    const apiUrl = `/hh/message_templates/${templateType}${topicId ? `?topic_id=${topicId}` : ''}`;
    const response = await api.get(apiUrl);
    return (response.data?.mail?.text ?? response.data?.text) || '';
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedAction(null);
      setSelectedTemplate(null);
      setMessage('');
      setProcessingProgress({ current: 0, total: 0 });
      setPrefillTemplate(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !initialActionId || responses.length === 0) return;
    const firstActions = responses[0].actions || [];
    const common = responses.length === 1
      ? firstActions
      : firstActions.filter((action: any) =>
          responses.every((r: any) => r.actions?.some((a: any) => a.id === action.id))
        );
    const action = common.find((a: any) => a.id === initialActionId);
    if (action) {
      setSelectedAction(action);
      
      const actionTemplates = action.templates || [];
      let templateToSelect: any = null;
      
      if (initialTemplateId) {
        templateToSelect = actionTemplates.find((t: any) => t.id === initialTemplateId);
      }
      if (!templateToSelect && actionTemplates.length > 0) {
        templateToSelect = actionTemplates[0];
      }
      
      setSelectedTemplate(templateToSelect || null);
      const phoneInterviewAction = firstActions.find((item: any) => item.id === 'phone_interview');
      const phoneInterviewTemplate = initialTemplateId
        ? (phoneInterviewAction?.templates || []).find((template: any) => template.id === initialTemplateId) ?? null
        : null;
      setPrefillTemplate(
        initialTemplateId && String(templateToSelect?.id ?? '') !== String(initialTemplateId)
          ? phoneInterviewTemplate
          : null
      );
    }
  }, [isOpen, initialActionId, initialTemplateId, responses]);

  useEffect(() => {
    const templateForText = prefillTemplate || selectedTemplate;

    if (!templateForText?.id && !templateForText?.name && !templateForText?.url) return;
    setLoadingTemplateText(true);
    loadTemplateText(templateForText)
      .then((text) => setMessage(text))
      .catch(() => setMessage(''))
      .finally(() => setLoadingTemplateText(false));
  }, [prefillTemplate, selectedTemplate?.id, selectedTemplate?.name, selectedTemplate?.url]);

  const getCommonActions = () => {
    if (responses.length === 0) return [];
    const firstActions = responses[0].actions || [];
    if (responses.length === 1) return firstActions;
    return firstActions.filter((action: any) => 
      responses.every(r => r.actions?.some((a: any) => a.id === action.id))
    );
  };

  const INVITE_ACTION_IDS = ['phone_interview', 'interview'];

  const handleSendAction = async () => {
    if (!selectedAction || responses.length === 0) return;

    setProcessingAction(true);
    const total = responses.length;
    setProcessingProgress({ current: 0, total });

    const isInviteAction = INVITE_ACTION_IDS.includes(selectedAction.id);
    let successCount = 0;
    let errorCount = 0;
    const successfulResponseIds: string[] = [];

    for (let i = 0; i < responses.length; i++) {
      const response = responses[i];
      setProcessingProgress({ current: i + 1, total });

      try {
        const responseId = response.id;
        const responseVacancyId = response.sourceVacancyId || vacancyId;
        const actionForResponse = response.actions?.find((a: any) => a.id === selectedAction.id) || null;
        const actionUrl = actionForResponse?.url || selectedAction.url;
        const actionMethod = actionForResponse?.method || selectedAction.method;

        const resumeId = response.resume?.id;

        // Для invite-actions без resumeId — пропускаем, не отправляем в ai-recruiter мусор
        if (isInviteAction && !resumeId) {
          console.warn('Skipping invite: no resumeId for response', response.id);
          errorCount++;
          continue;
        }

        const candidateName = [response.resume?.first_name, response.resume?.last_name].filter(Boolean).join(' ') || response.resume?.title;

        await api.post(`/vacancies/${responseVacancyId}/responses/${responseId}/action`, {
          actionId: selectedAction.id,
          actionUrl,
          actionMethod,
          message: message.trim() || undefined,
          templateId: selectedTemplate?.id || undefined,
          sendSms: true,
          resumeId: resumeId || undefined,
          candidateName: candidateName || undefined,
          firstName: response.resume?.first_name || undefined,
          lastName: response.resume?.last_name || undefined,
          middleName: response.resume?.middle_name || undefined,
          positionId: positionId || undefined,
        });

        successCount++;
        successfulResponseIds.push(responseId);
      } catch (error: any) {
        console.error(`Error performing action for response ${response.id}:`, error);
        errorCount++;
      }
    }

    setProcessingAction(false);

    if (successCount > 0) {
      if (isInviteAction) {
        // Для invite-action — только один информативный toast
        const msg = isBulkMode
          ? `${successCount} кандидатов добавлены в Диалоги со статусом «Одобрен»`
          : 'Кандидат добавлен в Диалоги со статусом «Одобрен»';
        toast.success(msg, { duration: 4000 });
      } else {
        // Обычный toast для не-invite действий
        if (isBulkMode) {
          toast.success(`${selectedAction.name} выполнено для ${successCount} кандидатов`);
        } else {
          toast.success(`${selectedAction.name} выполнено успешно`);
        }
      }
      onRemoveResponses?.(successfulResponseIds);
    }
    if (errorCount > 0) {
      toast.error(`Ошибки при обработке ${errorCount} откликов`);
    }

    onClose();
    onSuccess?.();
  };

  if (!isOpen) return null;

  const actions = getCommonActions();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {isBulkMode ? 'Массовое действие над откликами' : 'Действие над откликом'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {isBulkMode 
              ? `Выбрано кандидатов: ${responses.length}`
              : responses[0]?.resume?.title || 'Кандидат'
            }
          </p>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {isBulkMode && (
            <div className="mb-4 max-h-24 overflow-y-auto">
              <label className="block text-sm font-medium text-gray-700 mb-2">Выбранные кандидаты:</label>
              <div className="flex flex-wrap gap-2">
                {responses.map((response: any) => (
                  <span key={response.id} className="inline-flex items-center px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-700">
                    {getResponseCandidateDisplayName(response)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Ссылки
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (!positionSlug) {
                    toast.error('Не удалось сформировать ссылку на HR-интервью');
                    return;
                  }
                  const base = window.location.origin.toString().replace(/\/+$/, '');
                  const userId = useAuthStore.getState().user?.id;
                  const recr = userId ? btoa(String(userId)) : null;
                  const url = recr ? `${base}/vacancy/${positionSlug}?recr=${recr}` : `${base}/vacancy/${positionSlug}`;
                  handleCopyLink('hr-interview', url);
                }}
                className="inline-flex items-center gap-2 px-3 h-10 rounded-[12px] text-sm font-medium bg-[#F6F8FA] text-[#0A0D14] border border-[#E6E8EC] hover:bg-[#EEF0F2] transition-colors"
              >
                {copiedLinkKey === 'hr-interview' ? 'Скопировано' : 'Запись на HR-интервью'}
                <LinkCopyCurrentIcon className="w-5 h-5 flex-shrink-0 text-[#0A0D14]" />
              </button>

              <button
                type="button"
                onClick={() => {
                  if (!positionId) {
                    toast.error('Не удалось сформировать ссылку на AI-скрининг');
                    return;
                  }
                  if (!hasQuestions) {
                    toast.error('Нет вопросов для AI-скрининга');
                    return;
                  }
                  const base = (process.env.REACT_APP_CANDIDATE_BASE_URL || window.location.origin).toString().replace(/\/+$/, '');
                  const url = `${base}/candidate/interview/${positionId}`;
                  handleCopyLink('ai-screening', url);
                }}
                className={[
                  'inline-flex items-center gap-2 px-3 h-10 rounded-[12px] text-sm font-medium transition-opacity',
                  aiScreeningEnabled ? 'bg-[#EFFAF6] text-[#38C793] hover:opacity-80' : 'bg-[#FEF3EB] text-[#F17B2C] hover:opacity-90'
                ].join(' ')}
              >
                <LinkCopyIcon className="w-5 h-5 flex-shrink-0" />
                {copiedLinkKey === 'ai-screening'
                  ? 'Скопировано'
                  : aiScreeningEnabled
                  ? 'Ссылка на AI-скрининг'
                  : 'AI-скрининг не подключен'}
              </button>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Сообщение {isBulkMode ? 'кандидатам' : 'кандидату'}
              </label>
              <span className="text-xs text-gray-400">Опционально</span>
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={loadingTemplateText 
                ? "Загрузка шаблона..."
                : isBulkMode 
                  ? "Напишите сообщение, которое будет отправлено всем выбранным кандидатам..."
                  : "Напишите персональное сообщение кандидату..."
              }
              disabled={loadingTemplateText}
              className="w-full h-32 px-4 py-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#E16349] focus:border-transparent text-sm"
            />
            <div className="text-right text-xs text-gray-400 mt-1">
              {message.length} символов
            </div>
          </div>

          {selectedAction?.templates && selectedAction.templates.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Выберите шаблон письма:
              </label>
              <select
                value={selectedTemplate?.id || ''}
                onChange={(e) => {
                  setPrefillTemplate(null);
                  const templateId = e.target.value;
                  if (!templateId) {
                    setSelectedTemplate(null);
                    setMessage('');
                    return;
                  }
                  const template = selectedAction.templates.find((t: any) => t.id === templateId);
                  setSelectedTemplate(template || null);
                }}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E16349] focus:border-transparent text-sm bg-white"
              >
                <option value="">Без шаблона</option>
                {selectedAction.templates.map((template: any) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {processingAction ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#E16349]"></div>
                  {isBulkMode 
                    ? `Обработка ${processingProgress.current} из ${processingProgress.total}...`
                    : 'Отправка...'
                  }
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Сообщение будет отправлено через HH.ru
                </>
              )}
            </div>
            <button
              onClick={handleSendAction}
              disabled={!selectedAction || processingAction}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#E16349] text-white font-medium rounded-xl hover:bg-[#c9573f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {processingAction ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Отправка...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  {isBulkMode ? `Отправить всем (${responses.length})` : 'Отправить действие'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponseActionModal;
